import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUpload } from "app/contexts/UploadContext";
import ExcelViewer from './ExcelViewer';
import { PdfViewer } from './PdfViewer';
import { ImageViewer } from './ImageViewer';

function getFileType(filename) {
  if (!filename) return null;
  const ext = filename.split('.').pop().toLowerCase();
  if (['xlsx', 'xls'].includes(ext)) return 'excel';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)) return 'image';
  return null;
}

export default function ViewDocument({ open, onClose, filename, projectCode }) {
  const { getDocument } = useUpload();
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open || !filename) return;
    if (fetchedRef.current) return;

    let isActive = true;

    const fetchFile = async () => {
      setLoading(true);
      try {
        setFileType(getFileType(filename));
        const buffer = await getDocument(filename, projectCode);
        if (isActive) {
          setArrayBuffer(buffer);
          fetchedRef.current = true;
        }
      } catch (err) {
        console.error(err);
        if (isActive) setArrayBuffer(null);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchFile();
    return () => {
      isActive = false;
    };
  }, [open, filename, projectCode]);

  useEffect(() => {
    if (!open) {
      fetchedRef.current = false;
      setArrayBuffer(null);
      setFileType(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {filename}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
            p: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        }}
        >
        {loading && (
            <div
            style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
            >
            <CircularProgress />
            </div>
        )}
        {!loading && arrayBuffer && fileType === 'excel' && (
            <div style={{ flex: 1, width: '100%', height: '100%' }}>
            <ExcelViewer document={arrayBuffer} />
            </div>
        )}
        {!loading && arrayBuffer && fileType === 'pdf' && (
            <div style={{ flex: 1, width: '100%', height: '100%' }}>
            <PdfViewer document={arrayBuffer} />
            </div>
        )}
        {!loading && arrayBuffer && fileType === 'image' && (
            <div style={{ flex: 1, width: '100%', height: '100%' }}>
            <ImageViewer document={arrayBuffer} />
            </div>
        )}
        </DialogContent>

    </Dialog>
  );
}
